// src/studio/pages/SourcesStage.jsx — /studio/:projectId/sources
//
// Stage 1: the SOW and the conversation around it (meeting notes, email
// threads, transcripts) — the studio's two real inputs, unified onto one
// page instead of the SOW living in its own detail page with Interactions
// buried as one of its tabs (see the redesign plan's Context section).
import { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { FileText, MessagesSquare, RefreshCw, Trash2, Upload, Plus, ClipboardCheck, Unlink, Eye } from "lucide-react";
import {
  SurfaceCard,
  FileDropzone,
  StatusBadge,
  EmptyState,
  SkeletonText,
  Button,
  DataTable,
  Alert,
  ConfirmModal,
} from "../../shared/ui";
import { studioSowApi, studioInteractionsApi, studioPlanningSowApi } from "../api/studioApi";
import InteractionDialog from "../components/InteractionDialog";
import InteractionViewerModal from "../components/InteractionViewerModal";

function PlanningSowOption({ projectId, onImported }) {
  const [availability, setAvailability] = useState(null);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    studioPlanningSowApi
      .availability(projectId)
      .then((data) => { if (!cancelled) setAvailability(data); })
      .catch(() => { if (!cancelled) setAvailability(null); });
    return () => { cancelled = true; };
  }, [projectId]);

  if (!availability?.available) return null;

  async function handleImport() {
    setImporting(true);
    setError(null);
    try {
      const imported = await studioPlanningSowApi.import(projectId);
      // Import only stores the draft's text (create_sow_from_text()) --
      // same as a file upload, it still needs the parse pass to extract
      // real sections/tables before the Sources card (or anything
      // downstream) has anything to show.
      await studioSowApi.parse(projectId, imported.sow_id);
      onImported();
    } catch (err) {
      setError(err?.response?.data?.message || "Import failed.");
    } finally {
      setImporting(false);
    }
  }

  return (
    <div className="mt-3 flex items-start gap-3 rounded-xl border border-brand-100 bg-brand-50 px-4 py-3">
      <ClipboardCheck size={16} className="text-brand-600 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-slate-800">
          Planning already has a drafted SOW for this engagement — <span className="font-medium">{availability.title}</span>.
        </p>
        {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
      </div>
      <Button size="sm" onClick={handleImport} loading={importing}>
        Import from Planning
      </Button>
    </div>
  );
}

function SowSection({ projectId }) {
  const [sow, setSow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [unlinkTarget, setUnlinkTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setSow(await studioSowApi.latest(projectId));
    } catch {
      setError("Failed to load the SOW.");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  // The parse endpoint now returns almost instantly with status "parsing"
  // (the real extraction runs as a background task on the backend) -- so
  // poll for the real outcome instead of waiting on one long request, same
  // idea as InteractionsSection's summarization poll below.
  useEffect(() => {
    if (sow?.status !== "parsing") return undefined;
    const interval = setInterval(async () => {
      try {
        setSow(await studioSowApi.latest(projectId));
      } catch {
        // transient poll failure -- try again on the next tick
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [sow?.status, projectId]);

  async function handleUpload(file) {
    setBusy(true);
    setError(null);
    try {
      const uploaded = await studioSowApi.uploadFile(projectId, file);
      setSow(uploaded);
      setSow(await studioSowApi.parse(projectId, uploaded.id));
    } catch {
      setError("Upload failed.");
    } finally {
      setBusy(false);
    }
  }

  async function handleReparse() {
    if (!sow) return;
    setBusy(true);
    setError(null);
    try {
      setSow(await studioSowApi.parse(projectId, sow.id));
    } catch {
      setError("Re-parse failed.");
    } finally {
      setBusy(false);
    }
  }

  async function handleUnlink() {
    await studioSowApi.remove(projectId, unlinkTarget.id);
    setUnlinkTarget(null);
    await load();
  }

  return (
    <SurfaceCard
      title="Statement of Work"
      subtitle="The primary document every generated document is scoped against."
      right={sow && (
        <div className="flex items-center gap-2">
          <Button size="sm" variant="secondary" onClick={handleReparse} disabled={busy || sow.status === "parsing"}>
            <RefreshCw size={12} /> Re-parse
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setUnlinkTarget(sow)} disabled={busy || sow.status === "parsing"}>
            <Unlink size={12} /> Unlink
          </Button>
        </div>
      )}
    >
      {loading ? (
        <SkeletonText lines={2} />
      ) : error ? (
        <Alert variant="danger">{error}</Alert>
      ) : sow ? (
        <div className="flex items-center gap-3">
          <div className="p-2 bg-brand-50 rounded-lg shrink-0">
            <FileText size={18} className="text-brand-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-800 truncate">{sow.filename}</p>
            <p className="text-xs text-slate-400 mt-0.5">
              {sow.sections_count ?? 0} sections · {sow.tables_count ?? 0} tables · {sow.images_count ?? 0} images
            </p>
          </div>
          <StatusBadge map="rds" status={sow.status} pulse={sow.status === "parsing"} />
        </div>
      ) : (
        <>
          <FileDropzone
            accept={[".pdf", ".docx"]}
            title="Upload the SOW"
            description="PDF or Word — this is what every stage below reads from."
            onSubmit={handleUpload}
            submitLabel="Upload & parse"
            submittingLabel="Uploading…"
          />
          <PlanningSowOption projectId={projectId} onImported={load} />
        </>
      )}

      <ConfirmModal
        open={Boolean(unlinkTarget)}
        entityLabel="SOW"
        name={unlinkTarget?.filename}
        confirmLabel="Unlink"
        onClose={() => setUnlinkTarget(null)}
        onConfirm={handleUnlink}
      >
        Its extracted sections and tables are removed. Every stage below that
        reads from this SOW will need it replaced (upload a file or import
        from Planning again) before generation can run.
      </ConfirmModal>
    </SurfaceCard>
  );
}

function InteractionsSection({ projectId }) {
  const [interactions, setInteractions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [viewing, setViewing] = useState(null);

  const load = useCallback(async () => {
    try {
      setInteractions(await studioInteractionsApi.getInteractions(projectId));
      setError(null);
    } catch {
      setError("Failed to load interactions.");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  // Poll while anything is still summarizing — same idea as the old
  // InteractionsTab, kept because it's genuinely useful here too.
  useEffect(() => {
    const hasPending = interactions.some(
      (i) => i.summarization_status === "pending" || i.summarization_status === "summarizing"
    );
    if (!hasPending) return undefined;
    const interval = setInterval(load, 10000);
    return () => clearInterval(interval);
  }, [interactions, load]);

  async function handleDelete(id) {
    setDeleting(id);
    try {
      await studioInteractionsApi.deleteInteraction(projectId, id);
      setInteractions((prev) => prev.filter((i) => i.id !== id));
    } catch {
      setError("Failed to delete that item.");
    } finally {
      setDeleting(null);
    }
  }

  async function handleRetry(id) {
    try {
      await studioInteractionsApi.summarizeInteraction(projectId, id);
      load();
    } catch {
      setError("Failed to retry summarization.");
    }
  }

  const table = {
    columns: [
      {
        header: "Title",
        accessorKey: "title",
        cell: ({ row }) => (
          <div>
            <p className="text-sm text-slate-800">{row.original.title}</p>
            {row.original.filename && <p className="text-xs text-slate-400">{row.original.filename}</p>}
          </div>
        ),
      },
      {
        header: "Type",
        accessorKey: "interaction_type",
        cell: ({ getValue }) => <span className="text-sm text-slate-600 capitalize">{(getValue() || "").replace(/_/g, " ")}</span>,
      },
      {
        header: "Date",
        accessorKey: "source_date",
        cell: ({ getValue }) => <span className="text-sm text-slate-500">{getValue() || "—"}</span>,
      },
      {
        header: "Status",
        accessorKey: "summarization_status",
        cell: ({ getValue }) => <StatusBadge map="rds" status={getValue()} />,
      },
      {
        header: "",
        id: "actions",
        align: "right",
        sortAllowed: false,
        cell: ({ row }) => (
          <div className="flex items-center justify-end gap-1">
            {row.original.summarization_status === "failed" && (
              <button
                type="button"
                onClick={() => handleRetry(row.original.id)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-brand-600 hover:bg-brand-50 transition-colors"
                aria-label="Retry summarization"
              >
                <RefreshCw size={13} />
              </button>
            )}
            <button
              type="button"
              onClick={() => setViewing(row.original)}
              className="p-1.5 rounded-lg text-slate-400 hover:text-brand-600 hover:bg-brand-50 transition-colors"
              aria-label={`View ${row.original.title}`}
            >
              <Eye size={13} />
            </button>
            <button
              type="button"
              onClick={() => handleDelete(row.original.id)}
              disabled={deleting === row.original.id}
              className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-40"
              aria-label={`Delete ${row.original.title}`}
            >
              <Trash2 size={13} />
            </button>
          </div>
        ),
      },
    ],
    rows: interactions,
  };

  return (
    <SurfaceCard
      className="mt-5"
      title="Conversation Context"
      subtitle="Meeting notes, email threads, transcripts — extra context the AI reads alongside the SOW."
      right={<Button size="sm" onClick={() => setDialogOpen(true)}><Plus size={12} /> Add context</Button>}
    >
      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

      {!loading && interactions.length === 0 ? (
        <EmptyState
          icon={MessagesSquare}
          title="No context added yet"
          description="Upload meeting notes or paste an email thread to give generation more to work with."
          action={<Button size="sm" onClick={() => setDialogOpen(true)}><Upload size={12} /> Add context</Button>}
        />
      ) : (
        <DataTable table={table} loading={loading} entriesPerPage={{ defaultValue: 5, entries: [5, 10, 25] }} />
      )}

      <InteractionDialog
        open={dialogOpen}
        projectId={projectId}
        onClose={() => setDialogOpen(false)}
        onSuccess={() => { setDialogOpen(false); load(); }}
      />

      <InteractionViewerModal
        projectId={projectId}
        interaction={viewing}
        onClose={() => setViewing(null)}
      />
    </SurfaceCard>
  );
}

export default function SourcesStage() {
  const { project } = useOutletContext();

  return (
    <div>
      <SowSection projectId={project.id} />
      <InteractionsSection projectId={project.id} />
    </div>
  );
}
